import React from 'react'
import { Helmet } from 'react-helmet-async'
import ContactForm41 from '../components/contact-form41'
import Footer from '../components/footer'
import DarkNav from '../components/dark-nav'
import './privacy-policy.css'

const PrivacyPolicy = (props) => {
  React.useEffect(() => {
    // Scroll to top when component mounts, or to the linked section when the URL has a #hash
    const target = window.location.hash ? document.getElementById(window.location.hash.slice(1)) : null;
    if (target) target.scrollIntoView();
    else window.scrollTo(0, 0);
  }, []);

  return (
    <div className="privacy-policy-container1">
      <Helmet>
        <title>Privacy Policy | DataFlows Australia</title>
        <meta name="description" content="Read DataFlows' Privacy Policy to understand how we collect, use, and protect your personal information when using our SMS platform." />
        <link rel="canonical" href="https://dataflows.com.au/privacy-policy" />
        <meta property="og:title" content="Privacy Policy | DataFlows Australia" />
        <meta property="og:description" content="How DataFlows collects, uses, and protects your personal information." />
      </Helmet>
      <DarkNav />
      <section className="privacy-policy-hero">
        <div className="privacy-policy-hero-content">
          <div className="privacy-policy-content-container">
            <div className="privacy-policy-text-container1">
              <h1 className="privacy-policy-text05">Privacy Policy</h1>
              <span className="privacy-policy-text06">
                Last Updated: October 6, 2026
              </span>
              <h2 className="privacy-policy-text07">1. Introduction</h2>
              <span className="privacy-policy-text08">
                Welcome to DataFlows. We respect your privacy and are committed to protecting your personal data. This privacy policy will inform you about how we look after your personal data when you visit our website and tell you about your privacy rights and how the law protects you.
              </span>
              <h2 className="privacy-policy-text09">2. Data We Collect</h2>
              <span className="privacy-policy-text10">
                We may collect, use, store and transfer different kinds of personal data about you including:
                <br /><br />
                • Identity Data: includes first name, last name, username or similar identifier.
                <br />
                • Contact Data: includes email address and telephone numbers.
                <br />
                • Technical Data: includes internet protocol (IP) address, browser type and version, time zone setting and location, browser plug-in types and versions, operating system and platform, and other technology on the devices you use to access this website.
                <br />
                • Usage Data: includes information about how you use our website and services.
              </span>
              <h2 className="privacy-policy-text11">3. How We Use Your Data</h2>
              <span className="privacy-policy-text12">
                We will only use your personal data when the law allows us to. Most commonly, we will use your personal data in the following circumstances:
                <br /><br />
                • To process and deliver the services you have requested.
                <br />
                • To manage our relationship with you.
                <br />
                • To improve our website, products/services, marketing, and customer relationships.
                <br />
                • To recommend products or services that may be of interest to you.
              </span>
              <span className="privacy-policy-text12">
                We do not sell or share your personal data, including your mobile phone number, with third parties for their own marketing purposes.
              </span>
              <h2 className="privacy-policy-text21">SMS / Text Messaging</h2>
              <span className="privacy-policy-text22">
                If you or a DataFlows customer's recipient opts in to receive SMS text messages through the DataFlows platform, we collect and use your mobile phone number solely to send the messages you have consented to receive (such as appointment reminders, account notifications, verification codes, or other messages from the business you are interacting with).
                <br /><br />
                Text messaging originator opt-in data and consent will not be shared with any third party for marketing or promotional purposes, under any circumstance.
                <br /><br />
                Message frequency will vary depending on the sender and the type of messages you have opted in to receive. Message and data rates may apply. You may opt out of receiving SMS messages at any time by replying STOP to any message you receive, or by contacting us using the details in Section 7 of this policy.
              </span>
              <h2 id="ai-assistant-connections" className="privacy-policy-text21">AI Assistant Connections</h2>
              <span className="privacy-policy-text22">
                You can connect your DataFlows account to AI assistants, such as ChatGPT and Codex by OpenAI, Claude by Anthropic, and Gemini CLI by Google, and other assistants as we add support for them. You sign in with your DataFlows login and approve the connection yourself. The assistant can then only act on that one account, within the permissions shown when you approved it. We never receive or store your password for the assistant, and the assistant never receives your DataFlows password or API key.
                <br /><br />
                What a connection can access. When you ask the assistant to, it can read or create the following, and only this:
                <br /><br />
                • Your SMS credit balance, and usage totals such as messages sent, credits used, delivered and failed counts, replies received, and your most used sender IDs.
                <br />
                • The sender IDs and phone numbers on your account.
                <br />
                • The names of your contact groups and how many subscribed contacts each has. It cannot see the individual contacts in a group.
                <br />
                • Messages you send or schedule through the connection: the recipients' phone numbers or the contact group, the message text, the sender, the send time and the delivery status.
                <br />
                • Replies from people you messaged through the connection: their phone number, the reply text and when it arrived.
                <br />
                • Your campaign results: campaign names, status, and how many messages were delivered, failed (with the reason) or are still pending. Campaign message text is not shared.
                <br /><br />
                Messages sent from your DataFlows dashboard or from other integrations, such as appointment reminders from practice management software, are not available to the assistant.
                <br /><br />
                How it is used and who receives it. We use this data only to carry out what you ask the assistant to do, such as sending a message or showing a delivery report. The results are sent to the company that provides the assistant you connected (for example, OpenAI for ChatGPT and Codex, or Anthropic for Claude) so it can answer you, and that company handles them under its own privacy policy. SMS messages are delivered through our carrier partners in the same way as messages sent from the dashboard. We don't sell this data or use it for advertising.
                <br /><br />
                What we keep and for how long. We keep a record of each connection and of which messages were sent or scheduled through it, alongside your normal message history, under the retention terms in Section 5. Sign-in tokens for a connection expire after one hour and are renewed automatically for up to 30 days of use. A scheduled message can be cancelled at any time before it sends.
                <br /><br />
                Your controls. You can disconnect at any time from the assistant's own settings, in the section that lists connected apps, plugins or connectors (in ChatGPT: Plugins, DataFlows SMS, then disconnect the account; in Claude: Customize, Connectors, DataFlows SMS, then Disconnect). You can also ask us to revoke a connection by emailing support@dataflows.com.au. Disconnecting stops all access straight away. Your other rights in Section 6 apply to this data too.
              </span>
              <h2 className="privacy-policy-text13">4. Data Security</h2>
              <span className="privacy-policy-text14">
                We have put in place appropriate security measures to prevent your personal data from being accidentally lost, used, or accessed in an unauthorized way, altered, or disclosed. We also limit access to your personal data to those employees, agents, contractors, and other third parties who have a business need to know.
              </span>
              <h2 className="privacy-policy-text15">5. Data Retention</h2>
              <span className="privacy-policy-text16">
                We will only retain your personal data for as long as necessary to fulfill the purposes we collected it for, including for the purposes of satisfying any legal, accounting, or reporting requirements.
              </span>
              <h2 className="privacy-policy-text17">6. Your Legal Rights</h2>
              <span className="privacy-policy-text18">
                Under certain circumstances, you have rights under data protection laws in relation to your personal data, including the right to:
                <br /><br />
                • Request access to your personal data.
                <br />
                • Request correction of your personal data.
                <br />
                • Request erasure of your personal data.
                <br />
                • Object to processing of your personal data.
                <br />
                • Request restriction of processing your personal data.
                <br />
                • Request transfer of your personal data.
                <br />
                • Right to withdraw consent.
              </span>
              <h2 className="privacy-policy-text19">7. Contact Us</h2>
              <span className="privacy-policy-text20">
                If you have any questions about this privacy policy or our privacy practices, please contact us using the form below.
              </span>
            </div>
          </div>
        </div>
      </section>
      
      <ContactForm41
        action={
          <React.Fragment>
            <span className="privacy-policy-form-button">Submit</span>
          </React.Fragment>
        }
        content1={
          <React.Fragment>
            <span className="privacy-policy-form-text">
              <span>
                Have a question about our privacy policy? Fill out the form below, and we will get back to you as soon as possible.
              </span>
            </span>
          </React.Fragment>
        }
        content2={
          <React.Fragment>
            <span className="privacy-policy-form-heading">
              Get in touch with us
            </span>
          </React.Fragment>
        }
        heading1={
          <React.Fragment>
            <span className="privacy-policy-form-title">Contact us</span>
          </React.Fragment>
        }
        rootClassName="contact-form41-root-class-name"
        contactForm41Id="PrivacyPolicyContactForm"
      ></ContactForm41>
      
      <Footer />
    </div>
  )
}

export default PrivacyPolicy 